import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Check, Shield, Zap, Globe2, Code2, Database, Cloud, Boxes, Wrench, Search, Sparkles } from "lucide-react";
import { COMPANY, SERVICES, STATS, INDUSTRIES, PROCESS, PROJECTS, FAQS, TESTIMONIALS } from "@/data/site";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { TechStackGrid, PartnersStrip } from "@/components/TechStack";
import TechBg from "@/components/TechBg";
import Counter from "@/components/Counter";

const serviceIcon = {
  "web-development": Code2,
  "mobile-apps": Boxes,
  "hosting": Cloud,
  "blockchain": Database,
  "seo": Search,
  "it-consulting": Shield,
  "automation": Zap,
  "brand-design": Sparkles,
};

const HAS_DETAIL = new Set(["web-development", "mobile-apps", "hosting", "blockchain", "seo"]);

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section data-testid="hero-section" className="relative overflow-hidden border-b border-[#1f1f22]">
        <TechBg />
        <div className="container-x relative pt-12 md:pt-20 pb-16 md:pb-20 z-10">
          <div className="grid md:grid-cols-[1.3fr_1fr] gap-10 lg:gap-16 items-center">
            <div>
              <div className="chip reveal mb-8" data-testid="hero-chip">
                <span className="live-dot" /> Established 2016 · Serving Clients Worldwide
              </div>
              <h1 className="font-display text-[clamp(40px,6.2vw,84px)] font-black uppercase leading-[0.94] tracking-[-0.02em] text-white reveal delay-1">
                Building <span className="text-[#DC1F33]">Modern</span><br />
                Digital Solutions<br />
                <span className="text-zinc-500">For Ambitious</span><br />
                Businesses.
              </h1>
              <p className="mt-8 text-lg text-zinc-400 max-w-xl reveal delay-2">
                Enterprise software, scalable web platforms, cloud infrastructure and Web3 — engineered for ambitious teams across the globe.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4 reveal delay-3">
                <Link to="/contact" data-testid="hero-cta-primary" className="btn-primary">
                  Get Free Consultation <ArrowUpRight size={16} />
                </Link>
                <Link to="/services" data-testid="hero-cta-secondary" className="btn-ghost">
                  Explore Services <ArrowRight size={16} />
                </Link>
              </div>
            </div>

            {/* Hero side card */}
            <div className="reveal delay-4">
              <div className="border border-[#27272A] bg-[#0E0E10] p-1">
                <div className="border border-[#1f1f22] p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div className="label-mono">jeetit.com / ops console</div>
                    <div className="flex gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-full bg-zinc-700" />
                      <span className="w-2.5 h-2.5 rounded-full bg-zinc-700" />
                      <span className="w-2.5 h-2.5 rounded-full bg-[#DC1F33]" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-px bg-[#1f1f22] border border-[#1f1f22]">
                    {[
                      { v: "99.99%", l: "Uptime" },
                      { v: "1,248", l: "Deployments" },
                      { v: "32.4k", l: "Requests/s" },
                      { v: "0", l: "Open incidents" },
                    ].map((s) => (
                      <div key={s.l} className="bg-[#0E0E10] p-5">
                        <div className="font-display text-3xl font-bold text-white">{s.v}</div>
                        <div className="label-mono mt-1">{s.l}</div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-zinc-300 flex items-center gap-2">
                        <Check size={14} className="text-[#DC1F33]" /> Deploy successful
                      </span>
                      <span className="label-mono">2s ago</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-zinc-300 flex items-center gap-2">
                        <Shield size={14} className="text-[#DC1F33]" /> Smart contract verified
                      </span>
                      <span className="label-mono">live</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-zinc-300 flex items-center gap-2">
                        <Cloud size={14} className="text-[#DC1F33]" /> AWS multi-AZ healthy
                      </span>
                      <span className="label-mono">ok</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Stat strip */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 border-t border-l border-[#1f1f22]">
            {[
              { v: 10, suffix: "+", l: "Years Active" },
              { v: 240, suffix: "+", l: "Projects Shipped" },
              { v: 98, suffix: "%", l: "Client Retention" },
              { v: 4.98, suffix: "", l: "Average Rating", decimals: 2 },
            ].map((s) => (
              <div key={s.l} data-testid={`stat-${s.l.toLowerCase().replace(/\W+/g, "-")}`} className="border-r border-b border-[#1f1f22] p-6 md:p-8">
                <Counter to={s.v} suffix={s.suffix} decimals={s.decimals || 0} className="font-display text-4xl md:text-5xl font-black text-white tracking-tight inline-block" />
                <div className="label-mono mt-2">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TECH STACK GRID with real SVG logos */}
      <section className="py-20 border-b border-[#1f1f22] bg-[#0B0B0D]">
        <div className="container-x">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="label-mono mb-3">// Modern Technology Stack</div>
              <h2 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight text-white leading-tight">Tools we ship with.</h2>
            </div>
          </div>
          <TechStackGrid />
        </div>
      </section>

      <PartnersStrip />

      {/* SERVICES */}
      <section data-testid="services-section" className="section border-b border-[#1f1f22]">
        <div className="container-x">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
            <div>
              <div className="label-mono mb-4">// 01 — What We Deliver</div>
              <h2 className="font-display text-4xl md:text-6xl font-black uppercase tracking-tight leading-[0.95] text-white max-w-2xl">
                Full-spectrum<br /><span className="text-zinc-500">technology services.</span>
              </h2>
            </div>
            <Link to="/services" data-testid="services-all-link" className="btn-ghost self-start md:self-end">
              All Services <ArrowRight size={16} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-l border-t border-[#1f1f22]">
            {SERVICES.map((s) => {
              const Icon = serviceIcon[s.slug] || Code2;
              const target = HAS_DETAIL.has(s.slug) ? `/services/${s.slug}` : "/services";
              return (
                <Link
                  to={target}
                  key={s.slug}
                  data-testid={`service-card-${s.slug}`}
                  className="grid-cell group"
                >
                  <div className="flex items-start justify-between mb-8">
                    <div className="w-12 h-12 border border-[#27272A] flex items-center justify-center group-hover:border-[#DC1F33] group-hover:text-[#DC1F33] transition-colors text-zinc-400">
                      <Icon size={20} />
                    </div>
                    <div className="label-mono">{s.code}</div>
                  </div>
                  <div className="font-display text-xl font-bold text-white mb-2 tracking-tight">{s.title}</div>
                  <div className="text-sm text-zinc-400 leading-relaxed">{s.short}</div>
                  <div className="mt-8 flex items-center gap-2 text-[#DC1F33] opacity-0 group-hover:opacity-100 transition-opacity label-mono">
                    Explore <ArrowUpRight size={14} />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY US — bento */}
      <section className="section border-b border-[#1f1f22]">
        <div className="container-x">
          <div className="grid lg:grid-cols-[1fr_1.2fr] gap-16">
            <div>
              <div className="label-mono mb-4">// 02 — Why Jeet IT</div>
              <h2 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tight leading-[0.95] text-white">
                A technology partner<br /><span className="text-[#DC1F33]">you can rely on.</span>
              </h2>
              <p className="mt-8 text-zinc-400 text-lg leading-relaxed max-w-md">
                Since 2016 we've combined a decade of engineering experience with the responsiveness of an in-house team — built scalable engines for companies worldwide, not generic websites.
              </p>
              <Link to="/about" data-testid="why-us-cta" className="btn-ghost mt-10">
                More About Us <ArrowRight size={16} />
              </Link>
            </div>

            <div className="grid grid-cols-2 border-l border-t border-[#1f1f22]">
              {[
                { icon: Shield, t: "Security-First", d: "Hardened infra, secure code, regular audits and SOC2-aligned practice." },
                { icon: Zap, t: "Speed of Shipping", d: "Weekly demos, lean process, measurable progress every sprint." },
                { icon: Globe2, t: "Global Reach", d: "Time-zone aligned engagements with clients across 4 continents and counting." },
                { icon: Wrench, t: "Long-Term Support", d: "SLA-backed maintenance — we're around long after launch." },
              ].map((x) => {
                const I = x.icon;
                return (
                  <div key={x.t} className="grid-cell">
                    <I className="text-[#DC1F33] mb-6" size={22} />
                    <div className="font-display text-lg font-bold text-white mb-2">{x.t}</div>
                    <div className="text-sm text-zinc-400 leading-relaxed">{x.d}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="section border-b border-[#1f1f22] bg-[#0B0B0D]">
        <div className="container-x">
          <div className="label-mono mb-4">// 03 — Industries We Serve</div>
          <h2 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tight text-white leading-[0.95] max-w-3xl">
            Trusted across every major sector.
          </h2>

          <div className="mt-14 flex flex-wrap gap-3">
            {INDUSTRIES.map((i) => (
              <div key={i} data-testid={`industry-${i.toLowerCase().replace(/\W+/g, "-")}`} className="border border-[#27272A] px-5 py-3 hover:border-[#DC1F33] hover:text-[#DC1F33] transition-colors text-zinc-300 font-display tracking-tight">
                {i}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="section border-b border-[#1f1f22]">
        <div className="container-x">
          <div className="label-mono mb-4">// 04 — Our Process</div>
          <h2 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tight text-white leading-[0.95] max-w-3xl">
            Precision from <span className="text-[#DC1F33]">start to scale.</span>
          </h2>

          <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 border-l border-t border-[#1f1f22]">
            {PROCESS.slice(0, 8).map((p) => (
              <div key={p.code} data-testid={`process-${p.code}`} className="grid-cell">
                <div className="font-display text-5xl font-black text-[#DC1F33] mb-6">{p.code}</div>
                <div className="font-display text-xl font-bold text-white mb-2 tracking-tight">{p.title}</div>
                <div className="text-sm text-zinc-400 leading-relaxed">{p.text}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PORTFOLIO */}
      {/* <section className="section border-b border-[#1f1f22]">
        <div className="container-x">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
            <div>
              <div className="label-mono mb-4">// 05 — Selected Work</div>
              <h2 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tight text-white leading-[0.95]">
                Case studies that<br /><span className="text-zinc-500">speak for themselves.</span>
              </h2>
            </div>
            <Link to="/portfolio" data-testid="home-portfolio-cta" className="btn-ghost self-start md:self-end">
              View Full Portfolio <ArrowRight size={16} />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#1f1f22] border border-[#1f1f22]">
            {PROJECTS.slice(0, 3).map((p) => (
              <Link key={p.slug} to="/portfolio" data-testid={`home-project-${p.slug}`} className="bg-[#0B0B0D] hover:bg-[#101012] card-hover p-1 group">
                <div className="aspect-[4/3] overflow-hidden border border-[#1f1f22]">
                  <img src={p.image} alt={p.title} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500 group-hover:scale-105" />
                </div>
                <div className="p-6">
                  <div className="label-mono mb-2">{p.category}</div>
                  <div className="font-display text-xl font-bold text-white mb-2 tracking-tight">{p.title}</div>
                  <div className="text-sm text-zinc-400 mb-5">{p.blurb}</div>
                  <div className="flex items-center justify-between border-t border-[#1f1f22] pt-4">
                    <span className="text-[#DC1F33] font-display font-bold text-sm">{p.metric}</span>
                    <span className="label-mono">{p.stack[0]}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section> */}

      {/* TESTIMONIALS */}
      <section className="section border-b border-[#1f1f22] bg-[#0B0B0D]">
        <div className="container-x">
          <div className="label-mono mb-4">// 05 — Clients</div>
          <h2 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tight text-white leading-[0.95]">What our clients say.</h2>

          <div className="mt-14 grid md:grid-cols-3 border-l border-t border-[#1f1f22]">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="grid-cell">
                <div className="font-display text-3xl text-[#DC1F33] mb-4">&ldquo;</div>
                <p className="text-zinc-200 leading-relaxed mb-8 text-[15px]">{t.quote}</p>
                <div>
                  <div className="font-display font-bold text-white">{t.name}</div>
                  <div className="label-mono">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section border-b border-[#1f1f22]">
        <div className="container-x grid lg:grid-cols-[1fr_1.4fr] gap-16">
          <div>
            <div className="label-mono mb-4">// 06 — FAQ</div>
            <h2 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tight text-white leading-[0.95]">Frequently asked.</h2>
            <p className="mt-6 text-zinc-400 max-w-md">Everything you need to know before starting your project with Jeet IT.</p>
          </div>

          <Accordion type="single" collapsible className="border-t border-[#1f1f22]">
            {FAQS.map((f, i) => (
              <AccordionItem
                value={`item-${i}`}
                key={i}
                data-testid={`faq-item-${i}`}
                className="border-b border-[#1f1f22] !border-x-0"
              >
                <AccordionTrigger className="font-display text-lg text-white py-6 hover:no-underline text-left tracking-tight">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-zinc-400 leading-relaxed pb-6 text-[15px]">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="section">
        <div className="container-x">
          <div className="relative border border-[#27272A] bg-gradient-to-br from-[#0E0E10] via-[#0E0E10] to-[#1A0B0F] p-1 overflow-hidden">
            <div className="relative border border-[#1f1f22] p-12 md:p-20 overflow-hidden">
              {/* Subtle grid pattern */}
              <div
                className="absolute inset-0 pointer-events-none opacity-40"
                style={{
                  backgroundImage:
                    "linear-gradient(rgba(255,255,255,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.035) 1px, transparent 1px)",
                  backgroundSize: "64px 64px",
                  WebkitMaskImage: "radial-gradient(ellipse at right bottom, #000 30%, transparent 75%)",
                  maskImage: "radial-gradient(ellipse at right bottom, #000 30%, transparent 75%)",
                }}
              />

              {/* Huge background watermark */}
              <div
                className="absolute -bottom-10 md:-bottom-16 -right-4 md:-right-10 font-display font-black uppercase text-white/[0.035] leading-[0.85] pointer-events-none select-none whitespace-nowrap"
                style={{ fontSize: "clamp(140px, 22vw, 320px)" }}
              >
                BUILD/SHIP
              </div>

              {/* Red glow orb */}
              <div className="absolute -bottom-40 -right-32 w-[520px] h-[520px] rounded-full bg-[#DC1F33] blur-[120px] opacity-25 pointer-events-none" />
              {/* Smaller secondary orb top-left */}
              <div className="absolute -top-32 -left-20 w-[360px] h-[360px] rounded-full bg-[#0F1626] blur-[110px] opacity-60 pointer-events-none" />

              {/* Floating availability chip */}
              <div className="absolute top-6 right-6 hidden md:flex">
                <div className="chip !text-[10px]">
                  <span className="live-dot" /> Accepting new projects · Q1 2026
                </div>
              </div>

              {/* Content */}
              <div className="relative">
                <div className="label-mono mb-4">// Ready when you are</div>
                <h2 className="font-display text-4xl md:text-6xl font-black uppercase tracking-tight text-white leading-[0.95] max-w-3xl">
                  Let's build your<br /><span className="text-[#DC1F33]">next project.</span>
                </h2>
                <p className="mt-6 text-zinc-400 text-lg max-w-xl">
                  Book a free 30-minute consultation. Walk away with a clear roadmap, honest timeline and transparent pricing — no obligations.
                </p>
                <div className="mt-10 flex flex-wrap gap-4">
                  <Link to="/contact" data-testid="home-final-cta" className="btn-primary">
                    Get Free Consultation <ArrowUpRight size={16} />
                  </Link>
                  <a href={COMPANY.whatsapp} target="_blank" rel="noreferrer" data-testid="home-final-whatsapp" className="btn-ghost">
                    Chat on WhatsApp
                  </a>
                </div>

                {/* Quick reassurance row */}
                <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl">
                  {[
                    { v: "< 24h", l: "Response time" },
                    { v: "Free",  l: "Discovery call" },
                    { v: "Fixed", l: "Transparent pricing" },
                    { v: "10+ yr", l: "Engineering track record" },
                  ].map((x) => (
                    <div key={x.l} className="border-l border-[#27272A] pl-4">
                      <div className="font-display text-2xl font-black text-white">{x.v}</div>
                      <div className="label-mono mt-1 text-[10px]">{x.l}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
